import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OutletProvider } from "@/context/OutletContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleRoute from "@/components/RoleRoute";
import PermGate from "@/components/PermGate";
import AppLayout from "@/components/AppLayout";
import OutletPOSLayout from "@/components/OutletPOSLayout";
import { Toaster } from "@/components/ui/sonner";

const AuthPage = lazy(() => import("@/pages/AuthPage"));
const QROrder = lazy(() => import("@/pages/QROrder"));
const KioskOrder = lazy(() => import("@/pages/KioskOrder"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const ItemClassificationAudit = lazy(() => import("@/pages/ItemClassificationAudit"));
const Purchases = lazy(() => import("@/pages/Purchases"));
const Sales = lazy(() => import("@/pages/Sales"));
const Production = lazy(() => import("@/pages/Production"));
const LotIntegrity = lazy(() => import("@/pages/LotIntegrity"));
const NutritionManagement = lazy(() => import("@/pages/NutritionManagement"));
const LateProduction = lazy(() => import("@/pages/LateProduction"));
const SimpleBOMBuilder = lazy(() => import("@/pages/SimpleBOMBuilder"));
const BomReview = lazy(() => import("@/pages/BomReview"));
const AllOrders = lazy(() => import("@/pages/AllOrders"));
const Setup = lazy(() => import("@/pages/Setup"));
const BillerLogin = lazy(() => import("@/pages/BillerLogin"));
const ItemWiseReport = lazy(() => import("@/pages/ItemWiseReport"));
const PermissionsAdmin = lazy(() => import("@/pages/PermissionsAdmin"));
const ReportsExtra = lazy(() => import("@/pages/ReportsExtra"));
const SupplyChain = lazy(() => import("@/pages/SupplyChain"));
const AiInsights = lazy(() => import("@/pages/AiInsights"));
const Forecast = lazy(() => import("@/pages/Forecast"));
const Procurement = lazy(() => import("@/pages/Procurement"));
const CycleCount = lazy(() => import("@/pages/CycleCount"));
const OutletPortal = lazy(() => import("@/pages/OutletPortal"));
const MonthlyPlanning = lazy(() => import("@/pages/MonthlyPlanning"));
const ProductionPeriod = lazy(() => import("@/pages/ProductionPeriod"));
const StockValue = lazy(() => import("@/pages/StockValue"));
const OperationalCatalogue = lazy(() => import("@/pages/OperationalCatalogue"));
const Accounts = lazy(() => import("@/pages/Accounts"));
const POS = lazy(() => import("@/pages/POS"));
const KDS = lazy(() => import("@/pages/KDS"));
const Outlets = lazy(() => import("@/pages/Outlets"));
const Labels = lazy(() => import("@/pages/Labels"));
const Batches = lazy(() => import("@/pages/Batches"));
const Settings = lazy(() => import("@/pages/Settings"));
const OutstandingReport = lazy(() => import("@/pages/OutstandingReport"));
const ConsumptionReport = lazy(() => import("@/pages/ConsumptionReport"));
const TradedMarginReport = lazy(() => import("@/pages/TradedMarginReport"));
const ProductionDigest = lazy(() => import("@/pages/ProductionDigest"));
const DayEndProduction = lazy(() => import("@/pages/DayEndProduction"));
const DailyClosing = lazy(() => import("@/pages/DailyClosing"));
const OutletMenuEditor = lazy(() => import("@/pages/OutletMenuEditor"));
const MenuRanks = lazy(() => import("@/pages/MenuRanks"));
const MenuCatalog = lazy(() => import("@/pages/MenuCatalog"));
const ParcelPackaging = lazy(() => import("@/pages/ParcelPackaging"));
const VendorPayables = lazy(() => import("@/pages/VendorPayables"));
const B2BReceivables = lazy(() => import("@/pages/B2BReceivables"));
const DeliveryNotes = lazy(() => import("@/pages/DeliveryNotes"));
const CentralKitchen = lazy(() => import("@/pages/CentralKitchen"));
const TaxInvoices = lazy(() => import("@/pages/TaxInvoices"));
const ConsumptionVariance = lazy(() => import("@/pages/ConsumptionVariance"));
const Permissions = lazy(() => import("@/pages/Permissions"));
const LiveShifts = lazy(() => import("@/pages/LiveShifts"));
const HandoverHistory = lazy(() => import("@/pages/HandoverHistory"));
const BusinessReports = lazy(() => import("@/pages/BusinessReports"));
const ReportsSalesHub = lazy(() => import("@/pages/ReportsSalesHub"));
const ReportsProfitHub = lazy(() => import("@/pages/ReportsProfitHub"));
const ReportsConsumptionHub = lazy(() => import("@/pages/ReportsConsumptionHub"));
const ReportsProductionHub = lazy(() => import("@/pages/ReportsProductionHub"));
const ReportsOutletsHub = lazy(() => import("@/pages/ReportsOutletsHub"));
const SalesReports = lazy(() => import("@/pages/SalesReports"));
const Handover = lazy(() => import("@/pages/Handover"));
const PrintGroupsAdmin = lazy(() => import("@/pages/PrintGroupsAdmin"));
const OutletPrinterSettings = lazy(() => import("@/pages/OutletPrinterSettings"));
const CategoryGroups = lazy(() => import("@/pages/CategoryGroups"));
const EightySixBoard = lazy(() => import("@/pages/EightySixBoard"));
const MonthlyOutletExpenses = lazy(() => import("@/pages/MonthlyOutletExpenses"));
const CostDashboard = lazy(() => import("@/pages/CostDashboard"));
const BatchTracePublic = lazy(() => import("@/pages/BatchTracePublic"));
const MorningPrepSheet = lazy(() => import("@/pages/MorningPrepSheet"));
const MarginWatchdog = lazy(() => import("@/pages/MarginWatchdog"));
const CompareOutlets = lazy(() => import("@/pages/CompareOutlets"));
const PhysicalMenuPDF = lazy(() => import("@/pages/PhysicalMenuPDF"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const AutoPO = lazy(() => import("@/pages/AutoPO"));
const LoadedCost = lazy(() => import("@/pages/LoadedCost"));
const Warehouses = lazy(() => import("@/pages/Warehouses"));
const WorkOrders = lazy(() => import("@/pages/WorkOrders"));
const WorkOrderDetail = lazy(() => import("@/pages/WorkOrderDetail"));
const StockEntries = lazy(() => import("@/pages/StockEntries"));
const PurchaseReceipts = lazy(() => import("@/pages/PurchaseReceipts"));
const StockLedger = lazy(() => import("@/pages/StockLedger"));
const StockAgeing = lazy(() => import("@/pages/StockAgeing"));
const TaxTemplates = lazy(() => import("@/pages/TaxTemplates"));
const BatchLineagePublic = lazy(() => import("@/pages/BatchLineagePublic"));
const OutletOrders = lazy(() => import("@/pages/OutletOrders"));
const OutletExpenses = lazy(() => import("@/pages/OutletExpenses"));
const OutletWastage = lazy(() => import("@/pages/OutletWastage"));
const OutletReports = lazy(() => import("@/pages/OutletReports"));
const OutletWorkspace = lazy(() => import("@/pages/OutletWorkspace"));
const OutletStock = lazy(() => import("@/pages/OutletStock"));
const OutletDayEnd = lazy(() => import("@/pages/OutletDayEnd"));
const QuickPurchase = lazy(() => import("@/pages/QuickPurchase"));
const AreaWorkspace = lazy(() => import("@/pages/AreaWorkspace"));
const AreaRecords = lazy(() => import("@/pages/AreaRecords"));
const AreaStock = lazy(() => import("@/pages/AreaStock"));
const OwnerControl = lazy(() => import("@/pages/OwnerControl"));
const OwnerControlRecords = lazy(() => import("@/pages/OwnerControlRecords"));
const BackupRecovery = lazy(() => import("@/pages/BackupRecovery"));

const OUTLET_WORKSPACE_ROLES = ["admin", "manager", "outlet_manager", "staff", "cashier", "outlet", "area_manager"];
const AREA_REPORT_ROLES = ["admin", "manager", "area_manager"];

function PrivateLayout({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user?.role === "area_manager" && !["/delivery-notes", "/quick-purchase", "/pos/expenses", "/pos/wastage"].includes(pathname) && !pathname.startsWith("/area-workspace")) {
    return <Navigate to="/area-workspace" replace />;
  }
  if (user && user !== false && ["outlet", "outlet_manager", "staff", "cashier", "area_manager"].includes(user.role)) {
    // Outlet users see the lean POS shell — never the central-kitchen sidebar.
    return (
      <ProtectedRoute>
        <OutletPOSLayout>{children}</OutletPOSLayout>
      </ProtectedRoute>
    );
  }
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

function OutletShell({ children, roles = OUTLET_WORKSPACE_ROLES }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user?.role === "area_manager" && !["/delivery-notes", "/quick-purchase", "/pos/expenses", "/pos/wastage"].includes(pathname) && !pathname.startsWith("/area-workspace")) return <Navigate to="/area-workspace" replace />;
  return (
    <ProtectedRoute>
      <RoleRoute roles={roles}>
        <OutletPOSLayout>{children}</OutletPOSLayout>
      </RoleRoute>
    </ProtectedRoute>
  );
}

function OutletRoute({ children }) {
  return (
    <ProtectedRoute>
      <RoleRoute roles={["outlet"]}>{children}</RoleRoute>
    </ProtectedRoute>
  );
}

// Role → where the user lands right after login. Keep in sync with
// /app/frontend/src/pages/AuthPage.jsx (single source of truth in future).
const LANDING_FOR_ROLE = {
  admin: "/",
  manager: "/reports/deep-dive",
  staff: "/pos",
  cashier: "/pos",
  outlet: "/pos",
  outlet_manager: "/outlet-workspace",
  area_manager: "/area-workspace",
  central_kitchen: "/central-kitchen",
};
const landingRouteFor = (role) => LANDING_FOR_ROLE[role] || "/";

function LoginGate({ children }) {
  const { user } = useAuth();
  if (user && user !== false) {
    return <Navigate to={landingRouteFor(user.role)} replace />;
  }
  return children;
}

function HomeRoute() {
  const { user } = useAuth();
  if (user && user !== false && user.role !== "admin") {
    // Non-admin users hitting "/" get bounced to their role's landing page.
    return <Navigate to={landingRouteFor(user.role)} replace />;
  }
  return <PrivateLayout><Dashboard /></PrivateLayout>;
}

function AppRoutes() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-lg font-medium">Loading...</div>
      </div>
    }>
      <Routes>
      <Route path="/order/:outletId" element={<QROrder />} />
      <Route path="/kiosk/:outletId" element={<KioskOrder />} />
      <Route
        path="/login"
        element={
          <LoginGate>
            <AuthPage mode="login" />
          </LoginGate>
        }
      />
      <Route path="/" element={<HomeRoute />} />
      <Route path="/owner-control" element={<PrivateLayout><RoleRoute roles={["admin"]}><OwnerControl /></RoleRoute></PrivateLayout>} />
      <Route path="/owner-control/records" element={<PrivateLayout><RoleRoute roles={["admin"]}><OwnerControlRecords /></RoleRoute></PrivateLayout>} />
      <Route path="/backup-recovery" element={<PrivateLayout><RoleRoute roles={["admin"]}><BackupRecovery /></RoleRoute></PrivateLayout>} />
      {/* Public batch trace — no auth, reached by scanning the QR on labels */}
      <Route path="/b/*" element={<BatchTracePublic />} />

      {/* Outlet POS app — lean shell, outlet-role only */}
      <Route path="/pos" element={<PrivateLayout><POS /></PrivateLayout>} />
      <Route path="/kds" element={<PrivateLayout><RoleRoute roles={["admin", "central_kitchen"]}><KDS /></RoleRoute></PrivateLayout>} />
      <Route path="/outlet-workspace" element={<OutletShell><OutletWorkspace /></OutletShell>} />
      <Route path="/area-workspace" element={<OutletShell roles={AREA_REPORT_ROLES}><AreaWorkspace /></OutletShell>} />
      <Route path="/area-workspace/records" element={<OutletShell roles={AREA_REPORT_ROLES}><AreaRecords /></OutletShell>} />
      <Route path="/area-workspace/stock" element={<OutletShell roles={AREA_REPORT_ROLES}><AreaStock /></OutletShell>} />
      <Route path="/outlet-stock" element={<OutletShell><OutletStock /></OutletShell>} />
      <Route path="/quick-purchase" element={<OutletShell><QuickPurchase /></OutletShell>} />
      <Route path="/pos/day-end" element={<OutletShell><OutletDayEnd /></OutletShell>} />
      <Route path="/pos/orders" element={<OutletShell><OutletOrders /></OutletShell>} />
      <Route path="/pos/expenses" element={<OutletShell><OutletExpenses /></OutletShell>} />
      <Route path="/pos/wastage" element={<OutletShell><OutletWastage /></OutletShell>} />
      <Route path="/pos/reports" element={<OutletShell><OutletReports /></OutletShell>} />
      <Route path="/outlet" element={<OutletShell><OutletPortal /></OutletShell>} />

      <Route path="/inventory" element={<PrivateLayout><Inventory /></PrivateLayout>} />
      <Route path="/item-classification" element={<PrivateLayout><RoleRoute roles={["admin"]}><ItemClassificationAudit /></RoleRoute></PrivateLayout>} />
      <Route path="/purchases" element={<PrivateLayout><Purchases /></PrivateLayout>} />
      <Route path="/sales" element={<PrivateLayout><Sales /></PrivateLayout>} />
      <Route path="/production" element={<PrivateLayout><Production /></PrivateLayout>} />
      <Route path="/production/late-entry" element={<PrivateLayout><LateProduction /></PrivateLayout>} />
      <Route path="/bom-builder" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><SimpleBOMBuilder /></RoleRoute></PrivateLayout>} />
      <Route path="/all-orders" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><AllOrders /></RoleRoute></PrivateLayout>} />
      <Route path="/setup" element={<PrivateLayout><Setup /></PrivateLayout>} />
      <Route path="/biller-login" element={<BillerLogin />} />
      <Route path="/reports/item-wise" element={<PrivateLayout><RoleRoute roles={["admin","manager"]}><PermGate perm="report_item_wise"><ItemWiseReport /></PermGate></RoleRoute></PrivateLayout>} />
      <Route path="/users-permissions" element={<PrivateLayout><RoleRoute roles={["admin","manager"]}><PermissionsAdmin /></RoleRoute></PrivateLayout>} />
      <Route path="/reports/deep-dive" element={<PrivateLayout><RoleRoute roles={["admin","manager"]}><PermGate perm="report_item_wise"><ReportsExtra /></PermGate></RoleRoute></PrivateLayout>} />
      <Route path="/supply-chain" element={<PrivateLayout><SupplyChain /></PrivateLayout>} />
      <Route path="/forecast" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><Forecast /></RoleRoute></PrivateLayout>} />
      <Route path="/procurement" element={<PrivateLayout><Procurement /></PrivateLayout>} />
      <Route path="/cycle-count" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><CycleCount /></RoleRoute></PrivateLayout>} />
      <Route path="/labels" element={<PrivateLayout><Labels /></PrivateLayout>} />
      <Route path="/batches" element={<PrivateLayout><Batches /></PrivateLayout>} />
      <Route path="/monthly-planning" element={<PrivateLayout><MonthlyPlanning /></PrivateLayout>} />
      <Route path="/production-period" element={<PrivateLayout><ProductionPeriod /></PrivateLayout>} />
      <Route path="/stock-value" element={<PrivateLayout><StockValue /></PrivateLayout>} />
      <Route path="/accounts" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><Accounts /></RoleRoute></PrivateLayout>} />
      <Route path="/outlets" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><Outlets /></RoleRoute></PrivateLayout>} />
      <Route path="/settings" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><Settings /></RoleRoute></PrivateLayout>} />
      <Route path="/outstanding" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><PermGate perm="report_sales"><OutstandingReport /></PermGate></RoleRoute></PrivateLayout>} />
      <Route path="/consumption" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><PermGate perm="report_item_wise"><ConsumptionReport /></PermGate></RoleRoute></PrivateLayout>} />
      <Route path="/traded-margin" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><PermGate perm="report_sales"><TradedMarginReport /></PermGate></RoleRoute></PrivateLayout>} />
      <Route path="/production-digest" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><ProductionDigest /></RoleRoute></PrivateLayout>} />
      <Route path="/day-end-production" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><DayEndProduction /></RoleRoute></PrivateLayout>} />
      <Route path="/daily-closing" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><DailyClosing /></RoleRoute></PrivateLayout>} />
      <Route path="/outlet-menu-editor" element={<PrivateLayout><RoleRoute roles={["admin"]}><OutletMenuEditor /></RoleRoute></PrivateLayout>} />
      <Route path="/menu-ranks" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><MenuRanks /></RoleRoute></PrivateLayout>} />
      <Route path="/menu-catalog" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><MenuCatalog /></RoleRoute></PrivateLayout>} />
      <Route path="/parcel-packaging" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><ParcelPackaging /></RoleRoute></PrivateLayout>} />
      <Route path="/vendor-payables" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><VendorPayables /></RoleRoute></PrivateLayout>} />
      <Route path="/b2b-receivables" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><B2BReceivables /></RoleRoute></PrivateLayout>} />
      <Route path="/central-kitchen" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><CentralKitchen /></RoleRoute></PrivateLayout>} />
      <Route path="/operational-catalogue" element={<PrivateLayout><RoleRoute roles={["admin"]}><OperationalCatalogue /></RoleRoute></PrivateLayout>} />
      <Route path="/operational-catalogue/boms/:recipeId/review" element={<PrivateLayout><RoleRoute roles={["admin"]}><BomReview /></RoleRoute></PrivateLayout>} />
      <Route path="/outlet-bom-builder" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "outlet_manager"]}><SimpleBOMBuilder /></RoleRoute></PrivateLayout>} />
      <Route path="/delivery-notes" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen", "outlet", "outlet_manager", "staff", "cashier", "area_manager"]}><DeliveryNotes /></RoleRoute></PrivateLayout>} />
      <Route path="/tax-invoices" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><TaxInvoices /></RoleRoute></PrivateLayout>} />
      <Route path="/consumption-variance" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><PermGate perm="report_item_wise"><ConsumptionVariance /></PermGate></RoleRoute></PrivateLayout>} />
      <Route path="/permissions" element={<PrivateLayout><RoleRoute roles={["admin"]}><Permissions /></RoleRoute></PrivateLayout>} />
      <Route path="/live-shifts" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><LiveShifts /></RoleRoute></PrivateLayout>} />
      <Route path="/handover-history" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><HandoverHistory /></RoleRoute></PrivateLayout>} />
      <Route path="/business-reports" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><PermGate perm="report_sales"><BusinessReports /></PermGate></RoleRoute></PrivateLayout>} />
      <Route path="/sales-reports" element={<PrivateLayout><RoleRoute roles={["admin"]}><PermGate perm="report_sales"><SalesReports /></PermGate></RoleRoute></PrivateLayout>} />
      <Route path="/pos/handover" element={<OutletShell><Handover /></OutletShell>} />
      <Route path="/print-groups" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><PrintGroupsAdmin /></RoleRoute></PrivateLayout>} />
      <Route path="/printer-settings" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><OutletPrinterSettings /></RoleRoute></PrivateLayout>} />
      <Route path="/category-groups" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><CategoryGroups /></RoleRoute></PrivateLayout>} />
      <Route path="/86-board" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "staff"]}><EightySixBoard /></RoleRoute></PrivateLayout>} />
      <Route path="/monthly-expenses" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><MonthlyOutletExpenses /></RoleRoute></PrivateLayout>} />
      <Route path="/cost-dashboard" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><CostDashboard /></RoleRoute></PrivateLayout>} />
      <Route path="/prep-sheet" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><MorningPrepSheet /></RoleRoute></PrivateLayout>} />
      <Route path="/margin-watchdog" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><MarginWatchdog /></RoleRoute></PrivateLayout>} />
      <Route path="/compare-outlets" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><CompareOutlets /></RoleRoute></PrivateLayout>} />
      <Route path="/menu-pdf" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><PhysicalMenuPDF /></RoleRoute></PrivateLayout>} />
      <Route path="/audit-log" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><AuditLog /></RoleRoute></PrivateLayout>} />
      <Route path="/auto-po" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><AutoPO /></RoleRoute></PrivateLayout>} />
      <Route path="/loaded-cost" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><LoadedCost /></RoleRoute></PrivateLayout>} />
      <Route path="/warehouses" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><Warehouses /></RoleRoute></PrivateLayout>} />
      <Route path="/work-orders" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><WorkOrders /></RoleRoute></PrivateLayout>} />
      <Route path="/work-orders/:id" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><WorkOrderDetail /></RoleRoute></PrivateLayout>} />
      <Route path="/lot-integrity" element={<PrivateLayout><RoleRoute roles={["admin"]}><LotIntegrity /></RoleRoute></PrivateLayout>} />
      <Route path="/nutrition" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><NutritionManagement /></RoleRoute></PrivateLayout>} />
      <Route path="/stock-entries" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><StockEntries /></RoleRoute></PrivateLayout>} />
      <Route path="/purchase-receipts" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><PurchaseReceipts /></RoleRoute></PrivateLayout>} />
      <Route path="/stock-ledger" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><StockLedger /></RoleRoute></PrivateLayout>} />
      <Route path="/stock-ageing" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><StockAgeing /></RoleRoute></PrivateLayout>} />
      <Route path="/tax-templates" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><TaxTemplates /></RoleRoute></PrivateLayout>} />
      <Route path="/lineage/:batchNo" element={<BatchLineagePublic />} />
      <Route path="/ai-insights" element={<PrivateLayout><AiInsights /></PrivateLayout>} />
      <Route path="/reports/sales" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><ReportsSalesHub /></RoleRoute></PrivateLayout>} />
      <Route path="/reports/profitability" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><ReportsProfitHub /></RoleRoute></PrivateLayout>} />
      <Route path="/reports/consumption" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><ReportsConsumptionHub /></RoleRoute></PrivateLayout>} />
      <Route path="/reports/production" element={<PrivateLayout><RoleRoute roles={["admin", "manager", "central_kitchen"]}><ReportsProductionHub /></RoleRoute></PrivateLayout>} />
      <Route path="/reports/outlets" element={<PrivateLayout><RoleRoute roles={["admin", "manager"]}><ReportsOutletsHub /></RoleRoute></PrivateLayout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <OutletProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </OutletProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
